import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem,
  Paper, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import {
  getCountryOptions, getStateOptions, getCityOptions, resolveCountryIso, NO_STATE_PLACEHOLDER,
} from '../utils/locationTree.js';

// Mirrors the server-side limits (main/models.py) so counters and the API agree.
export const DESCRIPTION_MAX = 500;
export const STATUS_MESSAGE_MAX = 200;

const ORG_TYPES = [
  'restaurant', 'clinic', 'doctor', 'company', 'store', 'home',
  'bank', 'ATM', 'school', 'factory', 'others',
];
const COUNTRY_OPTIONS = getCountryOptions();

const cardSx = {
  p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2,
};

const EMPTY = {
  name: '', description: '', phone_number: '', address_line1: '', address_line2: '',
  city: '', state: '', country: '', pincode: '', portfolio_site: '', type: 'others',
  accepting_appointments: true, status_message: '', status_message_until: '',
};

/**
 * Server timestamp -> the value <input type="datetime-local"> expects (local wall-clock).
 *
 * The API runs USE_TZ=False with TIME_ZONE=UTC, so it returns a NAIVE timestamp that actually
 * represents UTC (e.g. "2026-10-20T00:39:00", no offset). JS parses an offset-less ISO string as
 * LOCAL time, so without this the value shifted by the UTC offset on every round-trip — pick
 * 8:39 PM, save, and it reads back as 12:39 AM. Treat an offset-less value as UTC.
 */
export const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
    const d = new Date(hasZone ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

/** `days` from now, as the value <input type="datetime-local"> expects. */
const inDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalInput(d.toISOString());
};

/** Human summary of when bookings resume. */
const reopensOn = (localValue) => {
  if (!localValue) return '';
  try {
    return new Date(localValue).toLocaleString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

const Line = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

/**
 * Business profile + away mode for an org admin.
 *
 * Deliberately two different interaction patterns:
 *   * Business details are edited rarely -> read-only summary + an Edit dialog (matches the
 *     category-edit pattern elsewhere in the app, and keeps 12 inputs out of the page).
 *   * Away mode is toggled often AND its current state must be visible at a glance -> inline.
 *     An admin should see "you're away until Oct 27" on landing, not have to open a dialog.
 */
export default function OrgProfileCard({ orgId }) {
  const [saved, setSaved] = useState(EMPTY);      // what the server has
  const [form, setForm] = useState(EMPTY);        // dialog working copy
  const [countryIso, setCountryIso] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const d = await apiCall(ENDPOINTS.ORG_DETAIL(orgId));
      const next = {
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d?.[k] ?? EMPTY[k]])),
        accepting_appointments: d?.accepting_appointments !== false,
        status_message_until: toLocalInput(d?.status_message_until),
      };
      setSaved(next);
      setForm(next);
      setCountryIso(resolveCountryIso(next.country));
    } catch { /* leave blank; saving still works */ }
    setLoading(false);
  }, [orgId]);

  // Deferred (strict set-state-in-effect rule — load() sets state synchronously).
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
  }, [load]);

  const stateOptions = useMemo(() => (countryIso ? getStateOptions(countryIso) : []), [countryIso]);
  const cityOptions = useMemo(() => {
    if (!countryIso) return [];
    const sel = stateOptions.find((s) => s.label === form.state);
    return getCityOptions(countryIso, sel ? sel.isoCode : null);
  }, [countryIso, stateOptions, form.state]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const patch = async (body, okText) => {
    setSaving(true); setMsg(null);
    try {
      const d = await apiCall(ENDPOINTS.ORG_UPDATE_INFO(orgId), {
        method: 'PATCH', body: JSON.stringify(body),
      });
      setMsg({ severity: 'success', text: okText });
      // Re-read so the summary reflects anything the server normalised/truncated.
      const next = {
        ...saved, ...body,
        ...(d ? { status_message_until: toLocalInput(d.status_message_until) } : {}),
      };
      setSaved(next); setForm(next);
      return true;
    } catch (err) {
      setMsg({ severity: 'error', text: (err && err.message) || 'Could not save. Please try again.' });
      return false;
    } finally { setSaving(false); }
  };

  const saveDetails = async () => {
    const ok = await patch({
      name: form.name, description: form.description, phone_number: form.phone_number,
      address_line1: form.address_line1, address_line2: form.address_line2,
      city: form.city, state: form.state === NO_STATE_PLACEHOLDER ? '' : form.state,
      country: form.country, pincode: form.pincode,
      portfolio_site: form.portfolio_site, type: form.type,
    }, 'Business details saved. Map location updates shortly.');
    if (ok) setOpen(false);
  };

  const saveAway = () => patch({
    accepting_appointments: form.accepting_appointments,
    status_message: form.status_message,
    // Blank = no end date; the API turns '' into null.
    status_message_until: form.status_message_until
      ? new Date(form.status_message_until).toISOString() : '',
  }, form.accepting_appointments ? 'You are open for bookings again.' : 'Away mode is on.');

  if (loading) return <Paper elevation={0} sx={cardSx}><CircularProgress size={22} /></Paper>;

  const away = !form.accepting_appointments;
  const savedAway = !saved.accepting_appointments;

  return (
    <>
      <Paper elevation={0} sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Business profile</Typography>
            <Typography variant="caption" color="text.secondary">
              Shown to customers in search and on your booking page.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon fontSize="small" />}
                  onClick={() => { setForm(saved); setCountryIso(resolveCountryIso(saved.country)); setMsg(null); setOpen(true); }}>
            Edit
          </Button>
        </Stack>

        <Grid container spacing={1.5}>
          <Line label="Name" value={saved.name} />
          <Line label="Type" value={saved.type} />
          <Line label="Phone" value={saved.phone_number} />
          <Line label="Website" value={saved.portfolio_site} />
          <Line label="Address" value={[saved.address_line1, saved.address_line2].filter(Boolean).join(', ')} />
          <Line label="City / State" value={[saved.city, saved.state].filter(Boolean).join(', ')} />
          <Line label="Country" value={saved.country} />
          <Line label="Postal / ZIP" value={saved.pincode} />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>About</Typography>
            <Typography variant="body2">{saved.description || '—'}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* Away mode stays inline: it's a frequent toggle, and when it's ON the admin must see
            that immediately rather than discovering it inside a dialog. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>Away / holiday mode</Typography>
          {savedAway && <Chip size="small" label="Away" sx={{ height: 20, fontWeight: 700, bgcolor: '#fff8e1', color: '#8a6d00' }} />}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Pauses new bookings but keeps you listed in search — customers see your message instead
          of simply not finding you.
        </Typography>

        <FormControlLabel
          control={<Switch checked={away} onChange={(e) => setForm((f) => ({ ...f, accepting_appointments: !e.target.checked }))} />}
          label={<Typography variant="body2" fontWeight={600}>{away ? 'Away — not taking bookings' : 'Open — taking bookings'}</Typography>}
          sx={{ mb: 1 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth size="small" label="Message to customers"
              placeholder="Closed for vacation — back Oct 27"
              value={form.status_message}
              onChange={(e) => setForm((f) => ({ ...f, status_message: e.target.value.slice(0, STATUS_MESSAGE_MAX) }))}
              helperText={`${form.status_message.length}/${STATUS_MESSAGE_MAX}`}
              FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            {/* Presets first: the real task is "I'm away for a week", which shouldn't require
                operating a datetime widget. The picker stays as the custom escape hatch. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Reopen bookings
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {[['In 1 week', 7], ['In 2 weeks', 14], ['In 1 month', 30]].map(([label, days]) => (
                <Chip
                  key={label} label={label} size="small" clickable
                  onClick={() => setForm((f) => ({ ...f, status_message_until: inDays(days) }))}
                  variant="outlined"
                />
              ))}
              <Chip
                label="No end date" size="small" clickable variant="outlined"
                color={form.status_message_until ? 'default' : 'primary'}
                onClick={() => setForm((f) => ({ ...f, status_message_until: '' }))}
              />
            </Stack>
            <TextField
              fullWidth size="small" type="datetime-local" label="Or pick a date & time"
              InputLabelProps={{ shrink: true }}
              value={form.status_message_until} onChange={set('status_message_until')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {form.status_message_until
                ? `Bookings reopen automatically on ${reopensOn(form.status_message_until)}.`
                : 'Stays off until you switch it back on.'}
            </Typography>
          </Grid>
        </Grid>

        {msg && !open && <Alert severity={msg.severity} sx={{ mt: 2, borderRadius: 2 }}>{msg.text}</Alert>}

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={saveAway} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save availability'}
          </Button>
        </Box>
      </Paper>

      {/* --- Edit business details --- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit business details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Business name" value={form.name} onChange={set('name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Public phone"
                value={form.phone_number} onChange={set('phone_number')}
                placeholder="+1 819 580 0240"
                helperText="Include the country code"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline minRows={2} label="About this business"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, DESCRIPTION_MAX) }))}
                helperText={`${form.description.length}/${DESCRIPTION_MAX} — appears on your card in search results`}
                FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Address" value={form.address_line1} onChange={set('address_line1')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Address line 2 (optional)" value={form.address_line2} onChange={set('address_line2')} />
            </Grid>

            {/* Country -> State -> City, same cascade as the onboarding form so edits stay
                consistent with what geocoding expects. */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="org-country">Country</InputLabel>
                <Select
                  labelId="org-country" label="Country" value={countryIso}
                  onChange={(e) => {
                    const iso = e.target.value;
                    const label = (COUNTRY_OPTIONS.find((c) => c.isoCode === iso) || {}).label || '';
                    setCountryIso(iso);
                    setForm((f) => ({ ...f, country: label, state: '', city: '' }));
                  }}
                >
                  {COUNTRY_OPTIONS.map((c) => <MenuItem key={c.isoCode} value={c.isoCode}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!countryIso}>
                <InputLabel id="org-state">State / Province</InputLabel>
                <Select labelId="org-state" label="State / Province" value={form.state}
                        onChange={(e) => setForm((f) => ({ ...f, state: e.target.value, city: '' }))}>
                  {stateOptions.map((st) => <MenuItem key={st.label} value={st.label}>{st.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!form.state}>
                <InputLabel id="org-city">City</InputLabel>
                <Select labelId="org-city" label="City" value={form.city} onChange={set('city')}>
                  {cityOptions.map((ct) => <MenuItem key={ct.label} value={ct.label}>{ct.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Postal / ZIP" value={form.pincode}
                         onChange={set('pincode')} helperText="Improves map accuracy & search" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Website (optional)" placeholder="https://"
                         value={form.portfolio_site} onChange={set('portfolio_site')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="org-type">Business type</InputLabel>
                <Select labelId="org-type" label="Business type" value={form.type} onChange={set('type')}>
                  {ORG_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {msg && open && <Alert severity={msg.severity} sx={{ mt: 2, borderRadius: 2 }}>{msg.text}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveDetails} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
